/**
 * UI Slice
 * Global UI state management (modals, notifications, theme, etc.)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface Modal {
  id: string;
  isOpen: boolean;
  data?: any;
}

interface UIState {
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  notifications: Notification[];
  modals: Record<string, Modal>;
  loading: {
    global: boolean;
    upload: boolean;
  };
  uploadProgress: number;
}

// Initial state
const initialState: UIState = {
  theme: 'dark',
  sidebarOpen: true,
  notifications: [],
  modals: {},
  loading: {
    global: false,
    upload: false,
  },
  uploadProgress: 0,
};

/**
 * UI slice
 */
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /**
     * Toggle theme
     */
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },

    /**
     * Set theme
     */
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },

    /**
     * Toggle sidebar
     */
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },

    /**
     * Set sidebar state
     */
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },

    /**
     * Add notification
     */
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `${Date.now()}-${Math.random()}`,
      };
      state.notifications.push(notification);
    },

    /**
     * Remove notification
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },

    /**
     * Clear all notifications
     */
    clearNotifications: (state) => {
      state.notifications = [];
    },

    /**
     * Open modal
     */
    openModal: (
      state,
      action: PayloadAction<{ id: string; data?: any }>
    ) => {
      state.modals[action.payload.id] = {
        id: action.payload.id,
        isOpen: true,
        data: action.payload.data,
      };
    },

    /**
     * Close modal
     */
    closeModal: (state, action: PayloadAction<string>) => {
      if (state.modals[action.payload]) {
        state.modals[action.payload].isOpen = false;
      }
    },

    /**
     * Close all modals
     */
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key].isOpen = false;
      });
    },

    /**
     * Set global loading
     */
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload;
    },

    /**
     * Set upload loading
     */
    setUploadLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.upload = action.payload;
    },

    /**
     * Set upload progress
     */
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },

    /**
     * Reset upload progress
     */
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
      state.loading.upload = false;
    },
  },
});

export const {
  toggleTheme,
  setTheme,
  toggleSidebar,
  setSidebarOpen,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  closeAllModals,
  setGlobalLoading,
  setUploadLoading,
  setUploadProgress,
  resetUploadProgress,
} = uiSlice.actions;

export default uiSlice.reducer;